/**
 * Phase 8 T-25 §29.B — Clarification stage (Stage 1 of the Smart Match
 * four-stage flow).
 *
 * Layout (top → bottom):
 *   1. Tab row: `[ Job Description | Refine Search ]` — preserves intake.
 *   2. `<JdQuoteCard />` — user's original input, zinc-50 quote card.
 *   3. `<ParaphraseTurn />` — agent paraphrase with bolded keywords + single
 *      clarifying question (unless round cap hit).
 *   4. Composer (shared `<AgentComposer>`) — placeholder: "Add any
 *      refinements, or press Send to start sourcing".
 *   5. Inline `[Start looking →]` chip — visible at every moment, advances
 *      the thread to Stage 2 (stream).
 *
 * Round cap: after 2 clarification rounds, `suppressQuestion` is forwarded
 * to `<ParaphraseTurn>` and the composer still works for free-form
 * refinement but the agent stops asking.
 *
 * Confidence-based skip: §29.B says if `paraphrase.confidence >= 0.85`
 * we render a 300 ms `Ready to search. Anything else?` bar + [Go] +
 * [Edit first] instead of the full stage. The heuristic stub always
 * returns 0.7 today (see `<SmartMatchCanvasPage>`); the bar activates
 * once the paraphrase service starts emitting a real confidence score.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ArrowRight, ChevronDown, ChevronRight, Plus, Settings } from 'lucide-react';
import { AgentComposer, type AgentComposerHandle } from '@modules/agent/components/AgentComposer';
import type { ScopeId } from '@modules/agent/components/AgentScopePill';
import {
  INTAKE_GRADIENT_LIGHT,
  INTAKE_GRADIENT_DARK,
} from '@modules/agent/components/AssistantIntake';
import { AgentMark } from '@shared/components/AgentMark';
import { getLlamaEdgeBreakerReason } from '@core/ai/llamaChat';
import type {
  SmartMatchIntakeTab,
  SmartMatchJdEntry,
  SmartMatchMode,
  SmartMatchResumeEntry,
} from '../lib/smartMatchThreads';
import { buildInitialParaphrase } from '../lib/paraphraseStub';
import { JdQuoteCard } from './JdQuoteCard';
import { ParaphraseTurn } from './ParaphraseTurn';

const CLARIFICATION_ROUND_CAP = 1;

export interface ClarificationRound {
  /** Agent paraphrase (may include `**keyword**` markdown bolds). */
  paraphrase: string;
  /** Thought-for timer, in ms, for the turn header. */
  thoughtMs: number;
  /**
   * Post user-feedback (21-Apr) — the refinement text the user sent for
   * this round. Round 0 is seeded from `intake.prompt` (rendered in the
   * top `JdQuoteCard`) so this stays undefined for it; round 1+ carry
   * the composer payload so the thread reads like a real chat
   * ("user bubble → Copilot bubble → user bubble → …").
   */
  userRefinement?: string;
}

interface ClarificationStageProps {
  /** Thread id — used only for `data-*` tags and test hooks. */
  threadId: string;
  /** Original intake text (verbatim; shown in the quote card). */
  prompt: string;
  /** Intake tab the user was on; preserved when flipping the top toggle. */
  intakeTab: SmartMatchIntakeTab;
  /** Callback for the Job Description / Refine Search toggle. */
  onIntakeTabChange: (next: SmartMatchIntakeTab) => void;
  /**
   * Paraphrase rounds (ordered). The latest round is always rendered at
   * the top. Round 0 is the initial paraphrase; round 1 is after the user's
   * first refinement; round 2 is after the second refinement. Anything
   * beyond index 1 suppresses the clarifying question (round cap).
   */
  rounds: ClarificationRound[];
  /** Current round count (what the user has sent). Caps at 2. */
  roundCount: number;
  /**
   * Submit a refinement — increments roundCount and produces a new
   * `rounds[]` entry. If `isThinking` is true the composer swaps to
   * `[Stop]`.
   */
  onSubmitRefinement: (text: string) => void;
  /** Whether the agent is currently paraphrasing. Drives Send/Stop swap. */
  isThinking?: boolean;
  /** User-triggered "stop paraphrase" — cancels the current inference. */
  onStop?: () => void;
  /** Advance to Stage 2 (stream). Receives the scope the user selected. */
  onStartLooking: (scope: ScopeId) => void;
  /**
   * §9.2 — Start a fresh clarification conversation. Clears the round
   * history and re-seeds the initial paraphrase. Rendered as a "New chat"
   * button in the top bar when provided.
   */
  onNewChat?: () => void;

  // ── §29.E / §29.F variant props ─────────────────────────────────────────

  /**
   * The intake mode that created this thread. Drives which Stage 1 body
   * renders:
   *   - `'single'` (default, T-25) — JD quote card + single paraphrase turn.
   *   - `'multi'`                  — combined paraphrase (agentic) OR
   *                                  accordion of per-JD turns (manual).
   *   - `'bulk'`                   — seniority summary (agentic) OR short
   *                                  summary with `[Start scoring →]`
   *                                  only (manual).
   */
  mode?: SmartMatchMode;
  /**
   * Manual-review flag. When `true` with `mode === 'multi'`, renders the
   * accordion. When `true` with `mode === 'bulk'`, hides the composer +
   * suppresses the clarifying question.
   */
  manualReview?: boolean;
  /**
   * Surface the `[⚙ Manual review]` toggle at the top-right of Stage 1.
   * Only rendered when `mode === 'multi'`. Optional — absent = toggle
   * hidden (used by the bulk flow and by pre-T-26 single threads).
   */
  onManualReviewChange?: (next: boolean) => void;
  /** §29.E — JDs to render in the multi-JD manual accordion. */
  multiJds?: SmartMatchJdEntry[];
  /** §29.F — uploaded resume metadata for the bulk summary variant. */
  bulkResumes?: SmartMatchResumeEntry[];
  /**
   * Override the composer+bottom-chip labels. Defaults:
   *   - mode single / multi-agentic → `"Start looking"`
   *   - multi-manual                → `"Start looking all"`
   *   - bulk-*                      → `"Start scoring"`
   */
  startLookingLabel?: string;
  /**
   * Intake source that was selected on the intake page. When 'external'
   * the scope pill in the composer defaults to 'external' so the user sees
   * "External" not "Org database" after they explicitly chose an external
   * channel on the previous screen.
   */
  intakeSource?: 'pool' | 'shared' | 'external';
}

export function ClarificationStage({
  threadId,
  prompt,
  intakeTab,
  onIntakeTabChange,
  rounds,
  roundCount,
  onSubmitRefinement,
  isThinking = false,
  onStop,
  onStartLooking,
  onNewChat,
  mode = 'single',
  manualReview = false,
  onManualReviewChange,
  multiJds = [],
  bulkResumes = [],
  startLookingLabel,
  intakeSource,
}: ClarificationStageProps) {
  const composerRef = useRef<AgentComposerHandle | null>(null);
  // §20.D scope pill — mirror the intake scope the user chose on the
  // previous screen so the pill reads "External" when they picked an
  // external channel, not the default "Org database".
  // Map IntakeSource → ScopeId so the pill options match (AgentScopePill
  // only knows 'org' | 'career' | 'external' — 'pool' and 'shared' are
  // internal pipeline values that were leaking into the pill and causing
  // a permanent fallback to "Org database").
  const [scope, setScope] = useState<ScopeId>(
    intakeSource === 'external' ? 'external' : intakeSource === 'shared' ? 'career' : 'org',
  );

  // Auto-fade rounds in as they arrive.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setMounted(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  /*
   * 21-Apr follow-up — auto-scroll the transcript to the newest turn.
   * Without this the conversation grew silently upward while the user
   * sat at the top of the thread with no way to see the composer
   * response (screenshot report). We keep a sentinel `<div ref />` at
   * the bottom of the chat body and `scrollIntoView` it every time the
   * round count or thinking-indicator state flips.
   */
  const scrollBodyRef = useRef<HTMLDivElement | null>(null);
  const chatBottomRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = chatBottomRef.current;
    if (!node) return;
    const id = window.requestAnimationFrame(() => {
      try {
        node.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } catch {
        scrollBodyRef.current?.scrollTo({
          top: scrollBodyRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [rounds.length, isThinking]);

  // Post user-feedback (21-Apr) — poll the llamaChat circuit breaker so
  // the "AI offline" banner appears as soon as the edge function starts
  // returning 502s / Groq rate-limit signals. We check every 2s rather
  // than every render to avoid re-renders driven purely by wall-clock
  // time. The reason string drives the banner copy (rate-limit vs. error).
  const [llmOfflineReason, setLlmOfflineReason] = useState<
    'rate-limit' | 'error' | null
  >(() => getLlamaEdgeBreakerReason());
  useEffect(() => {
    const iv = window.setInterval(() => {
      const reason = getLlamaEdgeBreakerReason();
      setLlmOfflineReason((prev) => (prev === reason ? prev : reason));
    }, 2000);
    return () => window.clearInterval(iv);
  }, []);

  const suppressQuestion = useMemo(
    () => roundCount >= CLARIFICATION_ROUND_CAP,
    [roundCount],
  );

  // Post user-feedback (21-Apr) — short confirmation phrases mean the
  // recruiter is done clarifying and wants sourcing to start. Without
  // this gate, typing "ok" / "yes" / "go" just spawned another round of
  // paraphrasing and the user got stuck in an infinite loop. Matched
  // case-insensitively, punctuation-tolerant.
  const CONFIRMATION_PHRASES = useMemo(
    () =>
      new Set([
        'ok',
        'okay',
        'k',
        'yes',
        'yep',
        'yup',
        'y',
        'sure',
        'sounds good',
        'looks good',
        'perfect',
        'great',
        'go',
        'go ahead',
        "let's go",
        'lets go',
        'start',
        'start sourcing',
        'start looking',
        'search',
        'find them',
        'find candidates',
        'proceed',
        'continue',
        'done',
      ]),
    [],
  );

  const isConfirmationPhrase = useCallback(
    (raw: string): boolean => {
      const normalized = raw.trim().toLowerCase().replace(/[!.?,]+$/g, '');
      return CONFIRMATION_PHRASES.has(normalized);
    },
    [CONFIRMATION_PHRASES],
  );

  /*
   * 21-Apr follow-up — an earlier round cap + a tight "exact-match
   * confirmation" check meant the agent happily paraphrased
   * "no tell me suitable candidates in our database or in linkedin
   * based on this jd" (screenshot) instead of advancing to search.
   * This second detector runs over longer phrases: if the user asks
   * to *see results* (tell me / show me / find / who fits / suitable
   * candidates / go with this / proceed) we skip paraphrasing and
   * hand off to `onStartLooking` directly.
   */
  const INTENT_PATTERNS = useMemo<RegExp[]>(
    () => [
      /\b(tell|show|give|list|find|fetch|get|surface|pull|bring)\s+(me|us|up)?\s*(the\s+)?(suitable|best|top|matching|matched|relevant)?\s*(candidates?|profiles?|resumes?|matches?|people|folks|engineers?|designers?|managers?)\b/i,
      /\bsuitable\s+candidates?\b/i,
      /\b(search|source|scan)\s+(the\s+)?(database|pool|pipeline|linkedin|network|our\s+db)\b/i,
      /\bwho\s+(fits|would\s+fit|matches|would\s+match)\b/i,
      /\b(go|proceed|continue|move|start)\s+(with|ahead|on)\s+(this|it|the\s+search)\b/i,
      /\b(show|display|render)\s+(me\s+)?(the\s+)?(results?|matches?)\b/i,
      /\bjust\s+(search|find|show|tell)\b/i,
    ],
    [],
  );
  const isIntentToSearch = useCallback(
    (raw: string): boolean => {
      const normalized = raw.trim();
      if (normalized.length === 0) return false;
      return INTENT_PATTERNS.some((re) => re.test(normalized));
    },
    [INTENT_PATTERNS],
  );

  const handleSend = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      // Empty Send → honour the placeholder copy ("press Send to start
      // sourcing") and advance directly.
      if (!trimmed) {
        onStartLooking(scope);
        return;
      }
      // Short confirmations skip paraphrasing and go straight to search.
      if (isConfirmationPhrase(trimmed)) {
        onStartLooking(scope);
        return;
      }
      // 21-Apr — user asked for results ("tell me candidates", "find
      // suitable profiles", "search linkedin", etc.). Advance to search
      // without another paraphrase round regardless of current round.
      if (isIntentToSearch(trimmed)) {
        onStartLooking(scope);
        return;
      }
      if (roundCount >= CLARIFICATION_ROUND_CAP) {
        // Round cap hit — still accept the refinement (parent chooses
        // whether to paraphrase or advance), but at this point the
        // bright [Start sourcing →] chip next to the composer is the
        // clearer path forward.
        onSubmitRefinement(text);
        return;
      }
      onSubmitRefinement(text);
    },
    [
      onSubmitRefinement,
      onStartLooking,
      roundCount,
      scope,
      isConfirmationPhrase,
      isIntentToSearch,
    ],
  );

  const tabDefs: Array<{ id: SmartMatchIntakeTab; label: string }> = [
    { id: 'job-description', label: 'Job Description' },
    { id: 'refine-search', label: 'Refine Search' },
  ];

  // Manual-multi and manual-bulk suppress the clarifying question and
  // the composer entirely — the user's only way forward is the
  // `[Start looking all →]` / `[Start scoring →]` chip.
  const isMultiManual = mode === 'multi' && manualReview;
  const isBulkManual = mode === 'bulk' && manualReview;
  const hideComposer = isBulkManual;
  const effectiveStartLabel =
    startLookingLabel ??
    (isMultiManual ? 'Start looking all' : mode === 'bulk' ? 'Start scoring' : 'Start looking');

  return (
    <div
      className="relative flex min-h-0 flex-1 flex-col transition-opacity"
      style={{
        height: 'calc(100dvh - 48px)',
        opacity: mounted ? 1 : 0,
        transitionDuration: '180ms',
      }}
      data-ggj-clarification-stage
      data-thread-id={threadId}
    >
      {/* Post user-feedback (21-Apr, follow-up) — during the clarify
          conversation we want the Wrangle-style pastel gradient ONLY on
          the top ~280px header band, not the full chat body. A mask
          fades the gradient out so the conversation below sits on a
          clean surface (image 2 reference). */}
      <div
        aria-hidden
        className="ggj-intake-gradient ggj-intake-gradient--light pointer-events-none absolute inset-x-0 top-0 h-[280px]"
        style={{
          background: INTAKE_GRADIENT_LIGHT,
          WebkitMaskImage: 'linear-gradient(180deg, #000 0%, #000 55%, rgba(0,0,0,0) 100%)',
          maskImage: 'linear-gradient(180deg, #000 0%, #000 55%, rgba(0,0,0,0) 100%)',
        }}
      />
      <div
        aria-hidden
        className="ggj-intake-gradient ggj-intake-gradient--dark pointer-events-none absolute inset-x-0 top-0 h-[280px]"
        style={{
          background: INTAKE_GRADIENT_DARK,
          WebkitMaskImage: 'linear-gradient(180deg, #000 0%, #000 55%, rgba(0,0,0,0) 100%)',
          maskImage: 'linear-gradient(180deg, #000 0%, #000 55%, rgba(0,0,0,0) 100%)',
        }}
      />

      {/* 1. Top tab row — Job Description / Refine Search */}
      <div
        className="relative sticky top-0 z-[5] flex h-11 shrink-0 items-center gap-2 border-b px-4"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          background: 'color-mix(in srgb, var(--vscode-bg-primary) 82%, transparent)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        <div
          role="tablist"
          aria-label="Intake mode"
          className="inline-flex rounded-[10px] border p-0.5 text-[12px] font-medium"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            background: 'var(--vscode-bg-elevated)',
          }}
        >
          {tabDefs.map((t) => {
            const active = intakeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onIntakeTabChange(t.id)}
                className={`inline-flex h-7 items-center rounded-[8px] px-3 transition-colors ${
                  active
                    ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
                    : 'text-[color:var(--vscode-text-secondary)] hover:text-[color:var(--vscode-text-primary)]'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* §9.2 — New chat: clears the round history and re-seeds the
              initial paraphrase. Hidden when the parent hasn't provided
              a handler (sandbox variants). */}
          {onNewChat ? (
            <button
              type="button"
              onClick={onNewChat}
              className="inline-flex h-7 items-center gap-1 rounded-[8px] border px-2 text-[11px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
              style={{
                borderColor: 'var(--vscode-border-primary)',
                color: 'var(--vscode-text-secondary)',
                background: 'transparent',
              }}
              aria-label="Start a new clarification conversation"
              title="Start a new clarification conversation"
            >
              <Plus className="h-3 w-3" aria-hidden />
              New chat
            </button>
          ) : null}
          {/* §29.E — [⚙ Manual review] toggle for Multi-JD. */}
          {mode === 'multi' && onManualReviewChange ? (
            <label
              className="inline-flex cursor-pointer items-center gap-1 rounded-[8px] border px-2 py-1 text-[11px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
              style={{
                borderColor: manualReview ? '#10B981' : 'var(--vscode-border-primary)',
                color: manualReview ? '#065F46' : 'var(--vscode-text-secondary)',
                background: manualReview ? 'var(--ggj-accent-soft,#D1FAE5)' : 'transparent',
              }}
            >
              <input
                type="checkbox"
                checked={manualReview}
                onChange={(e) => onManualReviewChange(e.target.checked)}
                className="sr-only"
              />
              <Settings className="h-3 w-3" aria-hidden />
              Manual review
            </label>
          ) : null}
          {/* Round counter deliberately removed — it made the UI feel
              like a mandatory multi-step wizard rather than a quick
              optional refinement before sourcing. */}
        </div>
      </div>

      {/* Scrollable body (quote card + paraphrase rounds) — narrower
          max-width (760px) reads more like a chat transcript than the
          previous wide canvas. Gap reduced to 3 units so agent + user
          turns sit closer together, matching Wrangle/ChatGPT density. */}
      <div
        ref={scrollBodyRef}
        className="relative min-h-0 flex-1 overflow-y-scroll ggj-scroll-thin"
      >
        <div className="mx-auto flex max-w-[760px] flex-col gap-3 px-6 py-6">
          {/* Post user-feedback (21-Apr) — surface edge-function outages.
              When `ggj_b2b_llama` has been failing (or Groq has rate-
              limited us) the breaker short-circuits future calls and
              paraphrase falls back to local AI / stub — this banner
              tells the user why the copy feels more generic. */}
          {llmOfflineReason ? (
            <div
              className="flex items-start gap-2 rounded-[10px] border px-3 py-2 text-[12px]"
              style={{
                borderColor: 'color-mix(in srgb, #F59E0B 40%, var(--vscode-border-primary))',
                background: 'color-mix(in srgb, #F59E0B 10%, var(--vscode-bg-elevated))',
                color: 'var(--vscode-text-primary)',
              }}
              role="status"
            >
              <AlertCircle
                className="mt-0.5 h-3.5 w-3.5 shrink-0"
                style={{ color: '#B45309' }}
                aria-hidden
              />
              <div className="flex-1">
                <div className="font-medium">
                  {llmOfflineReason === 'rate-limit'
                    ? 'Groq daily limit reached — using local AI'
                    : 'AI assistant temporarily unavailable'}
                </div>
                <div style={{ color: 'var(--vscode-text-secondary)' }}>
                  {llmOfflineReason === 'rate-limit'
                    ? 'Falling back to the on-device Ollama model. Quality may be lower; refinement and sourcing still work.'
                    : 'Using local paraphrase. You can keep refining or press the Start looking button to begin sourcing — results aren\u2019t affected.'}
                </div>
              </div>
            </div>
          ) : null}

          {/* 2. JD quote card — single / multi-agentic / bulk show the
              intake prompt verbatim. Multi-manual hides it in favour of
              per-JD accordion panels below. */}
          {!isMultiManual ? <JdQuoteCard prompt={prompt} /> : null}

          {/* §29.E Multi-JD — header strip showing the JD count. */}
          {mode === 'multi' && multiJds.length > 0 ? (
            <div
              className="flex items-center justify-between rounded-[10px] border px-3 py-2 text-[12px]"
              style={{
                borderColor: 'var(--vscode-border-primary)',
                background: 'var(--vscode-bg-elevated)',
                color: 'var(--vscode-text-secondary)',
              }}
            >
              <span>
                Multi-JD · {multiJds.length} JD{multiJds.length === 1 ? '' : 's'}
                {manualReview ? ' · manual review' : ' · agentic'}
              </span>
            </div>
          ) : null}

          {/* §29.F Bulk-resumes — header strip showing the file count. */}
          {mode === 'bulk' && bulkResumes.length > 0 ? (
            <div
              className="flex items-center justify-between rounded-[10px] border px-3 py-2 text-[12px]"
              style={{
                borderColor: 'var(--vscode-border-primary)',
                background: 'var(--vscode-bg-elevated)',
                color: 'var(--vscode-text-secondary)',
              }}
            >
              <span>
                Bulk résumés · {bulkResumes.length} file{bulkResumes.length === 1 ? '' : 's'}
                {manualReview ? ' · manual review' : ' · agentic'}
              </span>
            </div>
          ) : null}

          {/* 3. Body — variant-specific. */}
          {isMultiManual ? (
            <MultiJdManualAccordion jds={multiJds} />
          ) : rounds.length === 0 ? (
            <div
              className="rounded-[10px] border px-4 py-6 text-[13px]"
              style={{
                borderColor: 'var(--vscode-border-primary)',
                background: 'var(--vscode-bg-elevated)',
                color: 'var(--vscode-text-tertiary)',
              }}
              aria-live="polite"
            >
              Paraphrasing your input…
            </div>
          ) : (
            <>
              {rounds.map((r, idx) => {
                const isLatest = idx === rounds.length - 1;
                // Bulk-manual suppresses the clarifying question
                // regardless of round count.
                const suppress =
                  isBulkManual || (isLatest && suppressQuestion);
                return (
                  <div key={`round-${idx}`} className="ggj-fade-in flex flex-col gap-3">
                    {/* Post user-feedback (21-Apr) — render the user's
                        refinement as a chat bubble so the thread reads
                        like a conversation (Wrangle parity). Round 0
                        skips this because `intake.prompt` is already
                        shown as the `JdQuoteCard` at the top. */}
                    {r.userRefinement ? (
                      <UserRefinementBubble text={r.userRefinement} />
                    ) : null}
                    {/* An in-flight refinement appends a round with an
                        empty paraphrase; defer rendering `ParaphraseTurn`
                        until the LLM resolves so the bottom "Thinking…"
                        indicator reads as the response placeholder. */}
                    {r.paraphrase.trim().length > 0 ? (
                      <ParaphraseTurn
                        paraphrase={r.paraphrase}
                        thoughtMs={r.thoughtMs}
                        suppressQuestion={suppress}
                      />
                    ) : null}
                  </div>
                );
              })}
              {isThinking ? (
                // Chat-style typing indicator: agent avatar + three
                // bouncing dots in a bubble that matches the paraphrase
                // turn layout. This keeps the in-flight state feeling
                // like a real conversation rather than a standalone
                // status pill.
                <div className="flex items-start gap-2.5" aria-live="polite">
                  <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full shadow-sm"
                    style={{
                      background: 'var(--vscode-bg-elevated)',
                      border: '1px solid color-mix(in srgb, var(--ggj-accent,#10B981) 25%, var(--vscode-border-primary))',
                    }}
                    aria-hidden
                  >
                    <AgentMark className="h-4 w-4" ariaLabel={null} />
                  </div>
                  <div
                    className="inline-flex items-center gap-1.5 rounded-[14px] rounded-tl-[4px] border px-3.5 py-2.5 shadow-sm"
                    style={{
                      background: 'color-mix(in srgb, var(--vscode-bg-elevated) 92%, transparent)',
                      borderColor: 'color-mix(in srgb, var(--vscode-border-primary) 80%, transparent)',
                    }}
                  >
                    <span className="sr-only">Thinking…</span>
                    <span
                      className="ggj-typing-dot inline-block h-1.5 w-1.5 rounded-full"
                      style={{ background: 'var(--ggj-accent,#10B981)', animationDelay: '0ms' }}
                      aria-hidden
                    />
                    <span
                      className="ggj-typing-dot inline-block h-1.5 w-1.5 rounded-full"
                      style={{ background: 'var(--ggj-accent,#10B981)', animationDelay: '140ms' }}
                      aria-hidden
                    />
                    <span
                      className="ggj-typing-dot inline-block h-1.5 w-1.5 rounded-full"
                      style={{ background: 'var(--ggj-accent,#10B981)', animationDelay: '280ms' }}
                      aria-hidden
                    />
                  </div>
                </div>
              ) : null}
            </>
          )}

          {/* Bottom sentinel — scroll anchor for auto-stick-to-newest. */}
          <div ref={chatBottomRef} aria-hidden />
        </div>
      </div>

      {/* 4. Composer (pinned to bottom).
          Post user-feedback (21-Apr) — the `Start sourcing →` chip is
          now ALWAYS visible next to the composer, not only in the
          composer-hidden variants. Without it the user got stuck in
          an infinite clarification loop (see screenshot): every Send
          spawned another paraphrase instead of advancing to the actual
          search. Empty Send and short confirmation phrases ("ok",
          "yes", "go", etc.) are also routed straight to
          `onStartLooking` in `handleSend` above.
          Bulk-manual still hides the composer entirely; in that mode
          the chip is the only way forward. */}
      <div
        className="relative shrink-0 border-t"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          background: 'var(--vscode-bg-primary)',
        }}
      >
        <div className="mx-auto flex max-w-[760px] items-end gap-2 px-6 py-3">
          {!hideComposer ? (
            <>
              <div className="flex-1">
                <AgentComposer
                  ref={composerRef}
                  scope={scope}
                  onScopeChange={setScope}
                  onSend={(text) => handleSend(text)}
                  {...(onStop ? { onStop } : {})}
                  thinking={isThinking}
                  placeholder="Add any refinements, or press Send to start sourcing"
                />
              </div>
              <button
                type="button"
                onClick={() => onStartLooking(scope)}
                className="mb-[18px] inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[10px] px-3 text-[12px] font-semibold text-white transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--ggj-accent,#10B981)] focus:ring-offset-2"
                style={{
                  background: 'var(--ggj-accent,#10B981)',
                }}
                aria-label={`${effectiveStartLabel} — advance to sourcing`}
                title="Skip refinements and start searching candidates"
              >
                {effectiveStartLabel}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </button>
            </>
          ) : (
            <>
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => onStartLooking(scope)}
                className="mb-1 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors hover:bg-[var(--ggj-accent-soft,#D1FAE5)]"
                style={{
                  borderColor: 'var(--ggj-accent,#10B981)',
                  color: '#065F46',
                }}
                aria-label={`${effectiveStartLabel} — advance to sourcing`}
              >
                {effectiveStartLabel}
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
              </button>
            </>
          )}
        </div>
        {/* Hint strip — only shown after at least one paraphrase has
            landed so first-time users see the normal clarification
            flow but returning users (or anyone on round 2+) get a
            nudge that they can stop clarifying whenever. */}
        {rounds.length > 0 && !hideComposer ? (
          <div
            className="mx-auto max-w-[760px] px-6 pb-2 text-[11px]"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            Happy with the brief? Hit{' '}
            <span className="font-medium" style={{ color: 'var(--ggj-accent,#10B981)' }}>
              {effectiveStartLabel}
            </span>{' '}
            or just send a quick{' '}
            <span className="font-mono" style={{ color: 'var(--vscode-text-secondary)' }}>
              ok
            </span>{' '}
            to begin sourcing.
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ── User refinement bubble (21-Apr chat parity) ────────────────────────────

/**
 * A right-aligned "user said" bubble that mirrors Wrangle's chat pattern
 * (see image 4). Rendered once per `ClarificationRound.userRefinement`
 * so each refinement stays visible in the thread instead of vanishing
 * the moment Send is clicked.
 */
function UserRefinementBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <div
        className="max-w-[78%] rounded-[14px] rounded-tr-[4px] border px-3.5 py-2.5 text-[13.5px] leading-[1.55] whitespace-pre-wrap break-words shadow-sm"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          background: 'var(--vscode-bg-hover)',
          color: 'var(--vscode-text-primary)',
        }}
      >
        {text}
      </div>
    </div>
  );
}

// ── §29.E Multi-JD manual accordion ─────────────────────────────────────────

/**
 * One paraphrase turn per JD, each individually expandable. Per §29.E:
 * "Confirming any single JD does not start sourcing — sourcing begins
 * only when all are confirmed or the user clicks [Start looking all →]".
 * For T-26 we lean on the bottom `[Start looking all →]` chip; the
 * accordion is purely review surface.
 */
function MultiJdManualAccordion({ jds }: { jds: SmartMatchJdEntry[] }) {
  const [openIds, setOpenIds] = useState<Set<string>>(() => {
    // Default-expand the first JD so the user sees the pattern.
    return new Set(jds.length > 0 ? [jds[0]!.id] : []);
  });

  const togglePanel = useCallback((id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  if (jds.length === 0) {
    return (
      <div
        className="rounded-[10px] border px-4 py-6 text-[13px]"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          background: 'var(--vscode-bg-elevated)',
          color: 'var(--vscode-text-tertiary)',
        }}
      >
        No JDs to review.
      </div>
    );
  }

  return (
    <ul
      role="list"
      aria-label="Per-JD paraphrase accordion"
      className="flex flex-col gap-2"
    >
      {jds.map((jd, idx) => {
        const isOpen = openIds.has(jd.id);
        // Generate the per-JD paraphrase once; it's derived from the JD
        // text and is stable across renders (pseudo-random thoughtMs is
        // fine — the round stays mounted).
        const round = buildInitialParaphrase(jd.text);
        return (
          <li
            key={jd.id}
            className="rounded-[10px] border"
            style={{
              borderColor: 'var(--vscode-border-primary)',
              background: 'var(--vscode-bg-elevated)',
            }}
          >
            <button
              type="button"
              onClick={() => togglePanel(jd.id)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left"
              aria-expanded={isOpen}
              aria-controls={`ggj-multi-jd-panel-${jd.id}`}
            >
              {isOpen ? (
                <ChevronDown className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              )}
              <span
                className="text-[12px] font-medium"
                style={{ color: 'var(--vscode-text-primary)' }}
              >
                {jd.title || `JD ${idx + 1}`}
              </span>
              <span
                className="ml-auto text-[11px]"
                style={{ color: 'var(--vscode-text-tertiary)' }}
              >
                {jd.cancelled ? '— cancelled' : null}
              </span>
            </button>
            {isOpen ? (
              <div
                id={`ggj-multi-jd-panel-${jd.id}`}
                className="border-t px-3 py-3"
                style={{ borderColor: 'var(--vscode-border-primary)' }}
              >
                <JdQuoteCard prompt={jd.text} />
                <div className="mt-3">
                  <ParaphraseTurn
                    paraphrase={round.paraphrase}
                    thoughtMs={round.thoughtMs}
                    suppressQuestion={false}
                  />
                </div>
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
